import { Stack } from '@mui/material';
import { TagSelect, TagSelectProps } from '@schooly/style';
import { PropsWithChildren, useCallback, useState } from 'react';

import { ExpandedSelect } from './ExpandedSelect';
import { ExpandedSelectFooterButtons } from './ExpandedSelectContentContainer';

interface ExpandedSelectFilterProps<T> extends PropsWithChildren {
  selected: T[];
  onApply: (v: T[]) => void;
  onClear?: () => void;
  onClose: () => void;
  options: T[];
  getLabel: (v: T) => string;
}

export function ExpandedSelectFilter<T>({
  selected: initialSelected,
  onApply,
  onClose,
  onClear,
  options,
  getLabel,
}: ExpandedSelectFilterProps<T>) {
  const [selected, setSelected] = useState<T[]>(initialSelected);

  const onSelect = useCallback((v: T) => {
    setSelected((prev) => (prev.includes(v) ? prev.filter((item) => item !== v) : [...prev, v]));
  }, []);

  const handleApply = useCallback(() => {
    onApply(selected);
    onClose();
  }, [selected, onApply, onClose]);

  const renderContent = useCallback(() => {
    return (
      <Stack m={1} gap={2}>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {renderTags({
            items: options,
            tagProps: (item) => {
              return {
                variant: selected.includes(item) ? 'filled' : undefined,
                label: getLabel(item),
              };
            },
            onClick: onSelect,
            getLabel,
          })}
        </Stack>
        <ExpandedSelectFooterButtons onApply={handleApply} onClear={onClear} />
      </Stack>
    );
  }, [getLabel, onSelect, options, selected, handleApply, onClear]);

  return (
    <ExpandedSelect
      hasSelectedValue={selected.length > 0}
      onClose={onClose}
      onClear={onClear}
      renderContent={renderContent}
    >
      {renderTags({
        tagProps: { size: 'small' },
        items: selected,
        onDelete: onSelect,
        getLabel,
      })}
    </ExpandedSelect>
  );
}

type RenderTagsParams<T> = {
  items: T[];
  onDelete?: (v: T) => void;
  onClick?: (v: T) => void;
  getLabel: (v: T) => string;
  tagProps?: TagSelectProps | ((v: T) => TagSelectProps);
};

export function renderTags<T>({
  items,
  onDelete,
  onClick,
  tagProps,
  getLabel,
}: RenderTagsParams<T>) {
  return items.map((v) => (
    <TagSelect
      key={v as string}
      onClick={onClick ? () => onClick(v) : undefined}
      onDelete={onDelete ? () => onDelete(v) : undefined}
      label={getLabel(v)}
      {...(typeof tagProps === 'function' ? tagProps(v) : tagProps)}
    />
  ));
}
